import { Component, Input, OnChanges, OnInit, ViewChild, SimpleChanges, AfterViewInit } from '@angular/core';
import { RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MatTabsModule, MatTabChangeEvent } from '@angular/material/tabs';
import { EmaBatchViewDeltasComponent } from '../ema-batch-view-deltas/ema-batch-view-deltas.component';
import { EmaBatchViewErrorsComponent } from '../ema-batch-view-errors/ema-batch-view-errors.component';
import { ActivatedRoute, Router } from '@angular/router';
import { MatTabGroup } from '@angular/material/tabs';
import { Observable } from 'rxjs';
// take(1) ensures the shared row cache subscription completes after a single emission.
import { take } from 'rxjs/operators';
// Services used to resolve extractHdrId from the batch list API and in-memory row cache.
import { ApiService } from 'src/app/services/api.service';
import { ApiUrlService } from 'src/app/services/api-url.service';
import { DataService } from 'src/app/services/data.service';

export interface BatchHeaderTab {
  label: string;
  content: string;
}

export interface BreadcrumbItem {
  label: string;
  link: string | null;
}

// Covers the field name variations seen across SAFE payloads and the batch list API response.
interface BatchHeaderRecord {
  batch_id?: string;
  batchId?: string;
  extractHdrId?: string;
  extract_hdr_id?: string;
  entry_hdr_id?: string;
}

@Component({
  standalone: true,
  selector: 'ng-ema-batch-view',
  templateUrl: './ema-batch-view.component.html',
  styleUrls: ['./ema-batch-view.component.scss'],
  imports: [
    CommonModule,
    RouterModule,
    MatTabsModule,
    EmaBatchViewDeltasComponent,
    EmaBatchViewErrorsComponent,
  ]
})
export class EmaBatchViewComponent implements OnInit, AfterViewInit {

  @ViewChild(EmaBatchViewDeltasComponent) emaBatchViewDeltasComponent!: EmaBatchViewDeltasComponent;
  @ViewChild(EmaBatchViewErrorsComponent) emaBatchViewErrorsComponent!: EmaBatchViewErrorsComponent;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private apiService: ApiService,
    private apiUrlService: ApiUrlService,
    private dataService: DataService
  ) { }

  batchId: string | null = null;
  extractHdrId: string | null = null;
  breadcrumbs: BreadcrumbItem[] = [];

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      this.batchId = params.get('batchId');
      this.extractHdrId = this.getExtractHdrIdFromState();
      this.setupBreadcrumbs();
      this.resolveExtractHdrId();
    });
  }

  setupBreadcrumbs(): void {
    const lastLabel = `Batch - ${this.extractHdrId || this.batchId}`; // Fallback to batchId if extractHdrId is not available
    this.breadcrumbs = [
      { label: 'Home', link: '/' },
      { label: 'Batch Headers', link: '/partner/Amazon' },
      { label: lastLabel, link: null }
    ];
  }

  ngAfterViewInit(): void {
    // Activate the first tab on initial load
    setTimeout(() => {
      if (this.emaBatchViewDeltasComponent) {
        this.emaBatchViewDeltasComponent.onTabActivated();
      }
    }, 500);
  }

  // Resolves extractHdrId using three sources in order of cost:
  // 1. Router state (set on in-app navigation from the batch headers page)
  // 2. Shared row cache (avoids a network call if the batch headers page was visited)
  // 3. Fresh batchList API call (fallback for page refresh or direct links)
  private resolveExtractHdrId(): void {
    if (!this.batchId || this.extractHdrId) {
      return;
    }

    this.dataService.rowData$.pipe(take(1)).subscribe(rows => {
      const matchedRow = this.findBatchHeaderRecord(rows as BatchHeaderRecord[]);

      if (matchedRow) {
        this.extractHdrId = this.getExtractHdrId(matchedRow);
        this.setupBreadcrumbs();
        return;
      }

      this.fetchBatchHeaderRecord();
    });
  }

  private fetchBatchHeaderRecord(): void {
    const endpoint = this.apiUrlService.getUrl('gatewayBaseUrl', 'batchList');

    this.apiService.getRequest(endpoint).subscribe({
      next: (response: any) => {
        const rows = response.items || [];
        const matchedRow = this.findBatchHeaderRecord(rows);
        this.extractHdrId = matchedRow ? this.getExtractHdrId(matchedRow) : null;
        this.setupBreadcrumbs();
      },
      error: () => {
        this.extractHdrId = null;
      }
    });
  }

  private findBatchHeaderRecord(rows: BatchHeaderRecord[]): BatchHeaderRecord | undefined {
    return rows.find(row => this.getBatchId(row) === this.batchId);
  }

  private getBatchId(row: BatchHeaderRecord): string | null {
    return row.batch_id ?? row.batchId ?? null;
  }

  private getExtractHdrId(row: BatchHeaderRecord): string | null {
    return row.extractHdrId ?? row.extract_hdr_id ?? row.entry_hdr_id ?? null;
  }

  // Reads the extractHdrId passed through router navigation state.
  // This value is only present when navigating from the batch headers page; it is lost on refresh.
  private getExtractHdrIdFromState(): string | null {
    const state = history.state as { extractHdrId?: string };
    return state?.extractHdrId ?? null;
  }
  
  tabLoadTimes: Date[] = [];

  getTimeLoaded(index: number) {
    if (!this.tabLoadTimes[index]) {
      this.tabLoadTimes[index] = new Date();
    }

    return this.tabLoadTimes[index];
  }

  onTabChange(event: MatTabChangeEvent): void {
    // Add a longer delay to ensure tab transition is complete
    setTimeout(() => {
      switch (event.index) {
        case 0: // Updated Avails tab
          if (this.emaBatchViewDeltasComponent) {
            setTimeout(() => {
              this.emaBatchViewDeltasComponent.onTabActivated();
            }, 100);
          }
          break;
        case 1: // Errors tab
          if (this.emaBatchViewErrorsComponent) {
            setTimeout(() => {
              this.emaBatchViewErrorsComponent.onTabActivated();
            }, 100);
          }
          break;
      }
    }, 200); // Increased delay to ensure tab is fully rendered
  }

}